import HalfScreenWrapper from '@/components/HalfScreenWrapper';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import {useMediaQuery} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import {useTheme} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {SxProps} from '@mui/system';
import {lighten} from '@mui/system/colorManipulator';
import React, {FC, useState} from 'react';

import {useTranslation} from 'react-i18next';

export type LoginFormProps = {
  onLogin: (username: string, password: string) => void,
  isLoading: boolean,
  errorMessageKeys?: Array<string>,
}

const LoginForm: FC<LoginFormProps> = ({onLogin, isLoading, errorMessageKeys}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const textFieldSx: SxProps = {
    mb: 1,
    bgcolor: lighten(theme.palette.primary.main, 0.90)
  };

  const handleLoginClick = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(username, password);
  };

  return <HalfScreenWrapper>
    <Stack direction={isXs ? 'row' : 'column'} sx={{alignItems: 'center', justifyContent: 'center', mb: 6}}>
      <Avatar sx={styles.avatar}>
        <LockIcon sx={{color: 'common.white'}}/>
      </Avatar>
      <Typography component="h1" variant="h5" sx={{mb: 1}}>{t('authentication.login')}</Typography>
    </Stack>
    <Box component="form" onSubmit={handleLoginClick} noValidate>
      <TextField
        value={username}
        onChange={e => setUsername(e.target.value)}
        placeholder={t('authentication.userName')}
        required
        fullWidth
        slotProps={{input: {startAdornment: <AccountCircleIcon sx={styles.startAdornment}/>}}}
        sx={textFieldSx}
      />
      <TextField
        value={password}
        type="password"
        onChange={e => setPassword(e.target.value)}
        placeholder={t('authentication.password')}
        required
        fullWidth
        slotProps={{input: {startAdornment: <LockIcon sx={styles.startAdornment}/>}}}
        sx={textFieldSx}
      />
      <Stack direction="column">
        {
          errorMessageKeys?.map((errorMessageKey, index) => <Typography variant="body2" sx={styles.errorText} key={index}>
            # {errorMessageKey ? t(errorMessageKey) : ''}
          </Typography>)
        }
      </Stack>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={styles.loginButton}
        disabled={!username || !password || isLoading}
      >
        {isLoading ? `${t('authentication.loggingIn')}...` : t('authentication.login')}
      </Button>
    </Box>
  </HalfScreenWrapper>;
};

export default LoginForm;

const styles: Record<string, SxProps> = {
  avatar: {
    m: 1,
    bgcolor: 'primary.main'
  },
  errorText: {
    color: 'error.main',
    mb: 2,
    ml: 2
  },
  loginButton: {
    mt: 3,
    mb: 2,
    borderRadius: 0,
    height: '48px',
  },
  startAdornment: {
    color: 'primary.main',
    mr: 1,
    fontSize: '32px'
  }
};
